import type { Location } from "@sitescene/types";

import { isValidTimezone } from "@sitescene/shared";

import { isAllFilterIdsSelected } from "@/features/events/utils/filter-valid-ids";

/**
 * Outcome of resolving which IANA timezone should anchor the start/end time
 * bounds of the datetime span.
 *
 * Effort: avoid the previous bug where the browser's local TZ was used while
 * the events table displays installation/location wall-clock times (e.g. MDT).
 * Outcome: when a single timezone resolves, picked times mean "at the selected
 * site(s)", matching the UI. When timezone cannot resolve, query + matcher still
 * apply times in the browser local timezone (same helper).
 *
 * `pending` means location metadata is not loaded yet — do not treat as
 * blocked (would flash a warning on hard reload).
 * `error` means the locations query failed — show a warning, keep filters.
 */
export type TimeFilterZoneResolution =
	| { status: "ambiguous" }
	| { status: "error" }
	| { status: "ok"; timeZone: string }
	| { status: "pending" }
	| { status: "unavailable" };

/**
 * Copy shown when location timezone cannot safely anchor filter bounds
 * (mixed or missing location timezones). Kept in one place so banners stay
 * in sync. Filtering still works via browser-local fallback.
 */
export const TIME_FILTER_ZONE_BLOCKED_MESSAGE =
	"Times are interpreted in your browser timezone because selected locations do not share one timezone. Select one location (or locations in the same timezone) to filter by site local time.";

/**
 * Copy when locations failed to load so timezone cannot be resolved.
 */
export const TIME_FILTER_LOCATIONS_ERROR_MESSAGE =
	"Location timezones could not be loaded; times use your browser timezone. Refresh and try again.";

/**
 * True when a single valid location timezone is resolved (preferred for matching
 * the events table wall clock). Callers still apply times when this is false
 * via browser-local fallback in `buildDateTimeRangeTimestamps`.
 */
export function canApplyTimeOfDayFilters(
	resolution: TimeFilterZoneResolution,
): boolean {
	return resolution.status === "ok";
}

/**
 * Which locations participate in timezone resolution for time-of-day filters.
 *
 * - Empty `locationIds` → "all locations" (matches API: no locationIds param).
 * - Every org location selected → same as all.
 * - Otherwise → only the explicitly selected locations.
 */
export function getLocationsForTimeFilterZone(
	locationIds: readonly string[],
	allLocations: readonly Location[],
): Location[] {
	if (allLocations.length === 0) {
		return [];
	}

	const allIds = allLocations.map((location) => location.id);
	if (
		locationIds.length === 0 ||
		isAllFilterIdsSelected([...locationIds], allIds)
	) {
		return [...allLocations];
	}

	const selected = new Set(locationIds);
	return allLocations.filter((location) => selected.has(location.id));
}

/**
 * User-facing blocked copy for the current resolution.
 */
export function getTimeFilterZoneBlockedMessage(
	resolution: TimeFilterZoneResolution,
): string {
	if (resolution.status === "error") {
		return TIME_FILTER_LOCATIONS_ERROR_MESSAGE;
	}
	return TIME_FILTER_ZONE_BLOCKED_MESSAGE;
}

/**
 * True when UI should show a timezone warning.
 * Includes locations query failure; excludes pending load.
 */
export function isTimeFilterZoneBlocked(
	resolution: TimeFilterZoneResolution,
): boolean {
	return (
		resolution.status === "ambiguous" ||
		resolution.status === "unavailable" ||
		resolution.status === "error"
	);
}

/**
 * Derive a single IANA timezone for datetime filter bounds from the location
 * filter.
 *
 * Outcomes:
 * - `ok` — every relevant location has the same valid timezone → prefer that timezone.
 * - `ambiguous` — two or more distinct timezones → warn; browser-local fallback.
 * - `unavailable` — no locations, or any relevant location lacks a valid timezone.
 *
 * Does not use the browser timezone for the `ok` path: displayed event times
 * use location.timezone. Callers that have not finished loading locations
 * should return `pending` instead of calling this with an empty list. Callers
 * that failed to load locations should return `error`.
 */
export function resolveTimeFilterZone(
	locationIds: readonly string[],
	allLocations: readonly Location[],
): TimeFilterZoneResolution {
	const locations = getLocationsForTimeFilterZone(locationIds, allLocations);

	if (locations.length === 0) {
		return { status: "unavailable" };
	}

	const timeZones = new Set<string>();
	for (const location of locations) {
		// Incomplete location timezone metadata cannot be treated as "one timezone".
		if (!location.timezone || !isValidTimezone(location.timezone)) {
			return { status: "unavailable" };
		}
		timeZones.add(location.timezone);
	}

	if (timeZones.size > 1) {
		return { status: "ambiguous" };
	}

	// timeZones.size === 1 after the empty/invalid early returns above.
	const [timeZone] = timeZones;
	return { status: "ok", timeZone: timeZone! };
}
